/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    if (customElements.get('sticky-atc')) return;

    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.initializeStickyAtc = this.initializeStickyAtc.bind(this);
        this.onMediaChange = this.updateImage.bind(this);
        this.onStickyResize = this.updateStickyClearance.bind(this);
        this.throttledOnScroll = throttle(this.handleScroll.bind(this));
        // ProductForm binds an existing form in super(); a newly created element
        // may not have its children yet, so remember which form is already bound.
        this.boundSubmitForm = theme.settings.afterAddToCart !== 'no-js' ? this.getSubmitForm() : null;
      }

      connectedCallback() {
        super.connectedCallback?.();
        this.initializeStickyAtc();
        if (!this.stickyInitialized && document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.initializeStickyAtc, { once: true });
        }
      }

      initializeStickyAtc() {
        if (!this.isConnected || this.stickyInitialized) return;

        // The customized section schema does not include .section-main-product.
        // Resolve the owning Shopify section, never an unrelated product elsewhere.
        const section = this.closest('.section-main-product, .shopify-section');
        const mainProductForm = section?.querySelector('product-form');
        const buyButtons = mainProductForm?.querySelector('.quantity-submit-row');
        if (!section || !mainProductForm || !buyButtons) return;

        this.section = section;
        this.buyButtons = buyButtons;
        this.mainProductForm = mainProductForm;
        this.mainProductSubmitForm = this.mainProductForm.getSubmitForm();
        this.submitForm = this.getSubmitForm();
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.mainProductForm.querySelector('variant-picker'));
        this.button = this.querySelector('.add-to-cart');

        if (theme.settings.afterAddToCart !== 'no-js' && this.submitForm && this.boundSubmitForm !== this.submitForm) {
          this.submitForm.addEventListener('submit', this.handleSubmit.bind(this));
          this.boundSubmitForm = this.submitForm;
        }

        this.stickyInitialized = true;
        window.addEventListener('scroll', this.throttledOnScroll, { passive: true });
        window.addEventListener('resize', this.throttledOnScroll, { passive: true });
        if (typeof ResizeObserver !== 'undefined') {
          this.stickyResizeObserver = new ResizeObserver(this.onStickyResize);
          this.stickyResizeObserver.observe(this);
        }
        this.section.addEventListener('on:media-gallery:change', this.onMediaChange);

        this.updateImage();
        this.updateAddToCartButton();
        this.handleScroll();
      }

      disconnectedCallback() {
        document.removeEventListener('DOMContentLoaded', this.initializeStickyAtc);
        window.removeEventListener('scroll', this.throttledOnScroll);
        window.removeEventListener('resize', this.throttledOnScroll);
        this.stickyResizeObserver?.disconnect();
        this.section?.removeEventListener('on:media-gallery:change', this.onMediaChange);
        this.stickyInitialized = false;
        this.updateStickyClearance();
        super.disconnectedCallback?.();
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        if (!this.stickyInitialized || !this.isConnected) return;
        this.mainProductSubmitForm = this.mainProductForm.getSubmitForm();
        this.submitForm = this.getSubmitForm();
        if (!this.mainProductSubmitForm || !this.submitForm) return;

        const variantId = this.mainProductSubmitForm.querySelector('input[name="id"]')?.value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductSubmitForm.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductSubmitForm.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // The main form is authoritative if variant markup was refreshed separately.
        const stickyVariantInput = this.submitForm.querySelector('input[name="id"]');
        if (stickyVariantInput) stickyVariantInput.value = variantId;

        // Clear copied data
        this.submitForm.querySelectorAll('[data-copied]').forEach((el) => el.remove());

        // Copy data
        const formData = new FormData(this.mainProductSubmitForm);
        for (const p of formData) {
          if (!this.submitForm.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.submitForm.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      handleScroll() {
        if (!this.stickyInitialized || !this.isConnected || !this.buyButtons.isConnected) return;
        const topOffset = document.querySelector('.pageheader--sticky')
          ? document.querySelector('.section-header')?.offsetHeight || 0 : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
        this.updateStickyClearance();
      }

      /**
       * Keep floating controls above the visible bar, including its bottom inset.
       */
      updateStickyClearance() {
        if (!document.body) return;
        let clearance = 0;
        if (!document.body.classList.contains('scrolled-to-bottom')) {
          document.querySelectorAll('sticky-atc:not(.sticky-atc--out)').forEach((sticky) => {
            if (!sticky.isConnected || !sticky.stickyInitialized) return;
            const height = sticky.getBoundingClientRect().height;
            if (!height) return; // Includes sticky bars hidden on mobile with display: none.
            const bottom = parseFloat(window.getComputedStyle(sticky).bottom) || 0;
            clearance = Math.max(clearance, Math.ceil(height + Math.max(0, bottom)));
          });
        }
        document.body.style.setProperty('--cacb-sticky-atc-clearance', `${clearance}px`);
      }

      /**
       * Updates the image.
       */
      updateImage() {
        if (!this.stickyInitialized || !this.isConnected || !this.imageContainer) return;
        const mainImage = this.mainProductForm.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the add to cart button text.
       */
      updateAddToCartButton() {
        // If there is no variant selected, show unavailable-text
        if (!this.stickyInitialized || !this.button) return;
        const variantId = this.mainProductForm.getSubmitForm()?.querySelector('input[name="id"]')?.value;
        if (!variantId) {
          this.button.innerHTML = this.button.dataset.unavailableText;
        }
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}

